import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * A custom hook that debounces a value by a specified delay.
 * Useful for optimizing performance in search inputs, API calls, etc.
 *
 * @param value - The value to debounce.
 * @param delay - The delay in milliseconds.
 * @returns An object containing the debounced value and a flush function.
 */
export const useDebouncedValue = <T>(value: T, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    timeoutRef.current = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [value, delay]);

  /**
   * Immediately updates the debounced value with the current value.
   */
  const flush = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setDebouncedValue(value);
  }, [value]);

  return { debouncedValue, flush };
};
